/**
 * Activity Feed View
 * Shows all notifications, shared parlays received, shared parlays sent, and friends.
 * Tabs: Notifications | Shared With Me | Shared By Me | Friends
 */

import LoadingSpinner from '@components/ui/LoadingSpinner';
import { TierBadge } from '@components/ui/TierBadge';
import UpgradeModal from '@components/ui/UpgradeModal';
import { useFeatureAccess } from '@hooks/useFeatureAccess';
import { useFriendRequests } from '@hooks/useFriendRequests';
import { useNotifications } from '@hooks/useNotifications';
import type { ReceivedShares } from '@hooks/useSharing';
import type { Notification, NotificationType } from '@parlay-picker/shared/notification';
import { Bell, CheckCheck, Loader2, Users } from 'lucide-react';
import { type ReactElement, useCallback, useEffect, useState } from 'react';
import { FriendsTab } from './feed/components/FriendsTab';
import { NotificationCard } from './feed/components/NotificationCard';
import { SharedByMeList } from './feed/components/SharedByMeList';
import { SharedWithMeList } from './feed/components/SharedWithMeList';

// =============================================================================
// Types
// =============================================================================

type FeedTab = 'notifications' | 'shared' | 'sent' | 'friends';

interface FilterCategory {
  label: string;
  types: NotificationType[] | null; // null = all
}

const FILTER_CATEGORIES: FilterCategory[] = [
  { label: 'All', types: null },
  {
    label: 'Settlement',
    types: ['bet_won', 'bet_lost', 'bet_pushed', 'bet_voided'],
  },
  {
    label: 'Monitoring',
    types: [
      'line_movement_alert',
      'sharp_signal_alert',
      'injury_alert',
      'slate_injury_alert',
      'slate_line_movement_alert',
      'slate_sharp_signal_alert',
    ],
  },
  {
    label: 'Live',
    types: [
      'game_starting',
      'progress_milestone',
      'line_crossed',
      'leg_busted',
      'halftime_summary',
    ],
  },
  {
    label: 'Social',
    types: [
      'followed_user_bet',
      'followed_user_settled',
      'parlay_shared_with_you',
      'parlay_shared_by_you',
      'friend_request_received',
      'friend_request_accepted',
    ],
  },
  {
    label: 'System',
    types: [
      'daily_summary',
      'weekly_recap',
      'streak_alert',
      'quota_warning',
      'subscription_upgraded',
      'subscription_downgraded',
      'subscription_cancelled',
      'subscription_payment_failed',
      'subscription_expiring',
      'subscription_renewed',
      'welcome',
      'account_linked',
      'account_unlinked',
      'edge_proven_above',
      'edge_proven_below',
      'edge_no_longer_proven',
    ],
  },
];

interface ActivityFeedViewProps {
  /** App's copy, so viewing a share here also clears the header and nav badges */
  receivedShares: ReceivedShares;
  onNavigateToParlay?: (parlayId: number) => void;
  /** A parlay shared with the viewer to show (a notification link), until onLinkedShareHandled */
  linkedSharedParlayId?: number | null;
  onLinkedShareHandled?: () => void;
}

// =============================================================================
// Date grouping
// =============================================================================

function groupByDate(notifications: Notification[]): [string, Notification[]][] {
  const groups: Record<string, Notification[]> = {};
  const now = new Date();
  const today = now.toDateString();
  const yesterday = new Date(now.getTime() - 86400000).toDateString();

  for (const notification of notifications) {
    const date = new Date(notification.createdAt);
    const dateStr = date.toDateString();
    const label =
      dateStr === today
        ? 'Today'
        : dateStr === yesterday
          ? 'Yesterday'
          : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

    if (!groups[label]) groups[label] = [];
    groups[label].push(notification);
  }
  return Object.entries(groups);
}

// =============================================================================
// Component
// =============================================================================

export function ActivityFeedView({
  receivedShares,
  onNavigateToParlay,
  linkedSharedParlayId = null,
  onLinkedShareHandled,
}: ActivityFeedViewProps): ReactElement {
  const [activeTab, setActiveTab] = useState<FeedTab>('notifications');
  const [activeFilter, setActiveFilter] = useState<string>('All');
  const {
    notifications,
    unreadCount,
    loading,
    hasMore,
    fetchNotifications,
    loadMore,
    markAsRead,
    markAllAsRead,
    dismiss,
  } = useNotifications();
  const { incomingTotal: friendRequestCount, reload: reloadFriendRequests } = useFriendRequests();
  const { isLocked } = useFeatureAccess();
  const shareLocked = isLocked('social_share');
  const [showShareUpgrade, setShowShareUpgrade] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  // Kept for this visit, since the link itself is cleared once the tab opens
  const [highlightedSharedParlayId, setHighlightedSharedParlayId] = useState<number | null>(null);

  const openSharedTab = useCallback(
    () => (shareLocked ? setShowShareUpgrade(true) : setActiveTab('shared')),
    [shareLocked],
  );

  useEffect(() => {
    if (linkedSharedParlayId === null) return;
    setHighlightedSharedParlayId(linkedSharedParlayId);
    openSharedTab();
    onLinkedShareHandled?.();
  }, [linkedSharedParlayId, openSharedTab, onLinkedShareHandled]);

  // Fetch notification list (optionally filtered by types)
  const fetchWithFilter = useCallback(
    async (filterLabel?: string) => {
      const label = filterLabel ?? activeFilter;
      const category = FILTER_CATEGORIES.find((c) => c.label === label);
      const types = category?.types ?? undefined;
      await fetchNotifications({ types: types ?? undefined });
    },
    [activeFilter, fetchNotifications],
  );

  // Fetch full notification list on first render
  useEffect(() => {
    if (!hasFetched) {
      fetchWithFilter().then(() => setHasFetched(true));
    }
  }, [hasFetched, fetchWithFilter]);

  // Fetch friend request count for badge
  useEffect(() => {
    reloadFriendRequests();
  }, [reloadFriendRequests]);

  const handleFilterChange = useCallback(
    (label: string) => {
      if (label === activeFilter) return;
      setActiveFilter(label);
      fetchWithFilter(label);
    },
    [activeFilter, fetchWithFilter],
  );

  const dateGroups = groupByDate(notifications);
  const hasUnread = unreadCount > 0;

  // Track cumulative card index for stagger animation across date groups
  let cardIndex = 0;

  const renderNotificationsContent = () => {
    if (!hasFetched && loading) {
      return (
        <div className="flex justify-center items-center py-12">
          <LoadingSpinner />
        </div>
      );
    }

    if (notifications.length === 0) {
      return (
        <div className="rebrand-empty-state p-8 sm:p-12 text-center max-w-lg mx-auto">
          <div className="w-16 h-16 rounded-2xl bg-theme-primary/10 flex items-center justify-center mx-auto mb-5">
            <Bell className="w-8 h-8 text-theme-primary" />
          </div>
          <h3 className="text-xl font-bold text-white mb-2">No notifications yet</h3>
          <p className="text-theme-text-tertiary">
            Notifications will appear here as you use the app — bet settlements, live game updates,
            and more.
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {dateGroups.map(([dateLabel, items]) => (
          <div key={dateLabel}>
            <h3 className="text-xs font-semibold text-theme-text-tertiary uppercase tracking-wider mb-3">
              {dateLabel}
            </h3>
            <div className="space-y-2">
              {items.map((notification) => {
                const delay = Math.min(cardIndex * 0.04, 0.3);
                cardIndex++;
                return (
                  <NotificationCard
                    key={notification.id}
                    notification={notification}
                    onMarkAsRead={markAsRead}
                    onDismiss={dismiss}
                    onNavigateToParlay={onNavigateToParlay}
                    animationDelay={delay}
                  />
                );
              })}
            </div>
          </div>
        ))}
        {hasMore && (
          <div className="flex justify-center pt-2 pb-4">
            <button
              type="button"
              onClick={loadMore}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-theme-primary hover:text-theme-primary-hover bg-theme-surface border border-theme-border rounded-lg hover:bg-theme-surface-hover transition-colors disabled:opacity-50 min-h-10"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading...
                </>
              ) : (
                'Load more'
              )}
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div data-testid="activity-feed-view" className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-100">Activity</h1>
          <p className="text-theme-text-secondary mt-1">Notifications and shared parlays</p>
        </div>
        {activeTab === 'notifications' && hasUnread && (
          <button
            type="button"
            onClick={markAllAsRead}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-theme-primary hover:text-theme-primary-hover transition-colors rounded-lg hover:bg-theme-surface-hover min-h-10"
          >
            <CheckCheck className="w-4 h-4" />
            Mark all read
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex border-b border-theme-border mb-6 overflow-x-auto scrollbar-none">
        <button
          type="button"
          onClick={() => setActiveTab('notifications')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap shrink-0 ${
            activeTab === 'notifications'
              ? 'text-theme-primary border-blue-400'
              : 'text-theme-text-secondary border-transparent hover:text-theme-text-secondary'
          }`}
        >
          Notifications
          {unreadCount > 0 && (
            <span className="px-1.5 py-0.5 text-xs font-bold bg-red-500 text-white rounded-full min-w-4.5 text-center">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={openSharedTab}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap shrink-0 ${
            activeTab === 'shared'
              ? 'text-theme-primary border-blue-400'
              : shareLocked
                ? 'text-theme-text-tertiary border-transparent'
                : 'text-theme-text-secondary border-transparent hover:text-theme-text-secondary'
          }`}
        >
          Shared With Me
          {shareLocked ? (
            <TierBadge feature="social_share" size="md" />
          ) : receivedShares.unviewedCount > 0 ? (
            <span className="px-1.5 py-0.5 text-xs font-bold bg-theme-primary-fill text-theme-on-primary-fill rounded-full min-w-[18px] text-center">
              {receivedShares.unviewedCount}
            </span>
          ) : null}
        </button>
        <button
          type="button"
          onClick={() => (shareLocked ? setShowShareUpgrade(true) : setActiveTab('sent'))}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap shrink-0 ${
            activeTab === 'sent'
              ? 'text-theme-primary border-blue-400'
              : shareLocked
                ? 'text-theme-text-tertiary border-transparent'
                : 'text-theme-text-secondary border-transparent hover:text-theme-text-secondary'
          }`}
        >
          Shared By Me
          {shareLocked && <TierBadge feature="social_share" size="md" />}
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('friends')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap shrink-0 ${
            activeTab === 'friends'
              ? 'text-theme-primary border-blue-400'
              : 'text-theme-text-secondary border-transparent hover:text-theme-text-secondary'
          }`}
        >
          <Users className="w-4 h-4" />
          Friends
          {friendRequestCount > 0 && (
            <span className="px-1.5 py-0.5 text-xs font-bold bg-theme-primary-fill text-theme-on-primary-fill rounded-full min-w-[18px] text-center">
              {friendRequestCount > 99 ? '99+' : friendRequestCount}
            </span>
          )}
        </button>
      </div>

      {/* Filter Chips (only on notifications tab) */}
      {activeTab === 'notifications' && (
        <div className="flex flex-wrap gap-2 mb-4" data-testid="notification-filter-chips">
          {FILTER_CATEGORIES.map((category) => {
            const isActive = activeFilter === category.label;
            return (
              <button
                key={category.label}
                type="button"
                data-testid={`notification-filter-${category.label.toLowerCase().replace(/\s+/g, '-')}`}
                data-active={isActive ? 'true' : 'false'}
                onClick={() => handleFilterChange(category.label)}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors min-h-8 ${
                  isActive
                    ? 'bg-theme-primary-fill text-theme-on-primary-fill'
                    : 'bg-theme-surface border border-theme-border text-theme-text-secondary hover:bg-theme-surface-hover hover:text-theme-text'
                }`}
              >
                {category.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'notifications' && renderNotificationsContent()}
      {activeTab === 'shared' && (
        <SharedWithMeList
          receivedShares={receivedShares}
          highlightParlayId={highlightedSharedParlayId}
        />
      )}
      {activeTab === 'sent' && <SharedByMeList />}
      {activeTab === 'friends' && <FriendsTab />}

      {showShareUpgrade && (
        <UpgradeModal
          mode="feature"
          feature="social_share"
          onDismiss={() => setShowShareUpgrade(false)}
        />
      )}
    </div>
  );
}
